//  ----------      START   IMPORT  BLOCK       ----------
import { useState, useCallback, useMemo } from 'react';
//  Context
import { useUser } from "../Context/UserContext";
//  Services
import AgendaService from "../Services/AgendaService";
import AgendaSearch from "../Services/AgendaSearch";
//  Utils
import { transformEvents, type FullCalendarEvent, type AnyAgendaEntry } from '../../Utils/calendarUtils';
//  Types
import type { 
    UserCreateAgendaParams, 
    ProfessionalCreateAgendaParams, 
    UpdateAgendaParams, 
    WeeklyAgendaResponse,
    AgendaEntryResponse
} from '../../Types/schema-helpers';
//  ----------      END   IMPORT  BLOCK       ----------

export interface UseAgendaReturn {
  agenda: WeeklyAgendaResponse | null;
  isLoading: boolean;
  error: string | null;
  fetchWeeklySchedule: (medicUsername: string, year: number, weekNumber: number) => Promise<WeeklyAgendaResponse | null>;
  createUserAppointment: (payload: UserCreateAgendaParams) => Promise<FullCalendarEvent | null>;
  createProfessionalAppointment: (payload: ProfessionalCreateAgendaParams) => Promise<FullCalendarEvent | null>;
  updateAgendaEntry: (entryId: string, updateData: UpdateAgendaParams) => Promise<FullCalendarEvent | null>;
  cancelAppointment: (entryId: string) => Promise<boolean>;
  searchMaskedPatientName: (query: string) => Promise<string | null>;
}

export const useUserAgenda = (): UseAgendaReturn => {
  const { agenda, setUserAgenda } = useUser();
  
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const service = useMemo(() => new AgendaService(), []);
  const searchService = useMemo(() => new AgendaSearch(), []);

  /**
   * Helper to process events into FullCalendar objects.
   * Keeps UTC 'Z' indicators intact so FullCalendar handles client timezone conversion properly.
   */
  const processCalendarEvent = (entry: AgendaEntryResponse): FullCalendarEvent | null => {
    const [transformedEvent] = transformEvents([entry as AnyAgendaEntry]);
    if (!transformedEvent) return null;

    return transformedEvent;
  };

  /**
   * Searches masked patient name by username or query string.
   */
  const searchMaskedPatientName = useCallback(
    async (query: string): Promise<string | null> => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await searchService.searchMaskedPatientName({ query });
        setIsLoading(false);
        return result;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to search patient.";
        console.error("Error searching masked patient name:", err);
        setError(message);
        setIsLoading(false);
        return null;
      }
    },
    [searchService]
  );

  /**
   * Fetches weekly schedule for a medic.
   */
  const fetchWeeklySchedule = useCallback(
    async (medicUsername: string, year: number, weekNumber: number): Promise<WeeklyAgendaResponse | null> => {
      setIsLoading(true);
      setError(null);

      if (!medicUsername || !year || !weekNumber) {
        setError("Medic username, year, and week number are required.");
        setIsLoading(false);
        return null;
      }

      try {
        const data = await service.fetchWeeklySchedule(medicUsername, year, weekNumber);
        console.log('data', data);
        setUserAgenda(data);
        setIsLoading(false);
        return data;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to load schedule.";
        console.error("Error fetching weekly schedule:", err);
        setError(message);
        setIsLoading(false);
        return null;
      }
    },
    [service, setUserAgenda]
  );

  /**
   * Creates an appointment as a Patient (User).
   */
  const createUserAppointment = useCallback(
    async (payload: UserCreateAgendaParams): Promise<FullCalendarEvent | null> => {
      console.log('payload', payload);
      setIsLoading(true);
      setError(null);
      try {
        const response = await service.createUserAppointment(payload);
        const transformedEvent = processCalendarEvent(response);
        setIsLoading(false);
        return transformedEvent;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to create user appointment.";
        console.error("Error creating user appointment:", err);
        setError(message);
        setIsLoading(false);
        throw new Error(message);
      }
    },
    [service]
  );

  /**
   * Creates an appointment directly as a Professional (Medic).
   */
  const createProfessionalAppointment = useCallback(
    async (payload: ProfessionalCreateAgendaParams): Promise<FullCalendarEvent | null> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await service.createProfessionalAppointment(payload);
        const transformedEvent = processCalendarEvent(response);
        setIsLoading(false);
        return transformedEvent;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to create professional appointment.";
        console.error("Error creating professional appointment:", err);
        setError(message);
        setIsLoading(false);
        throw new Error(message);
      }
    },
    [service]
  );

  /**
   * Updates an existing agenda entry.
   */
  const updateAgendaEntry = useCallback(
    async (entryId: string, updateData: UpdateAgendaParams): Promise<FullCalendarEvent | null> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await service.updateAgendaEntry(entryId, updateData);
        const transformedEvent = processCalendarEvent(response);
        setIsLoading(false);
        return transformedEvent;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to update appointment.";
        console.error(`Error updating appointment ${entryId}:`, err);
        setError(message);
        setIsLoading(false);
        throw new Error(message);
      }
    },
    [service]
  );

  /**
   * Deletes (cancels) an appointment by ID.
   */
  const cancelAppointment = useCallback(
    async (entryId: string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);
      try {
        await service.deleteAgendaEntry(entryId);
        setIsLoading(false);
        return true;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to cancel appointment.";
        console.error("Error canceling appointment:", err);
        setError(message);
        setIsLoading(false);
        throw new Error(message);
      }
    },
    [service]
  );

  return {
    agenda,
    isLoading,
    error,
    fetchWeeklySchedule,
    createUserAppointment,
    createProfessionalAppointment,
    updateAgendaEntry,
    cancelAppointment,
    searchMaskedPatientName,
  };
};